import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import {
  Loader2, X, Save, Download, Upload, Search, Phone, Mail, Clock, ShoppingBag,
  Repeat, FileText, Feather, UserPlus, ClipboardList, Send, MessageSquare,
  Star, Tag, Plus, ArrowUp, ArrowDown, ChevronsUpDown, Users, CircleDollarSign,
  KeyRound, Trash2, AlertTriangle,
} from "lucide-react";

const LS_TOKEN = "bnm-admin-token";
const LS_RECENT = "bnm-crm-recent";

const TYPE_STYLE = {
  immersion_client: "bg-gold-deep/15 text-gold-deep",
  orientation_client: "bg-sage/25 text-sage-deep",
  vault_member: "bg-ink text-ivory-50",
  book_owner: "bg-sage/20 text-sage-deep",
  immersion_applicant: "bg-gold/15 text-gold-deep",
  prospect: "bg-ivory-200 text-ink-muted",
};
const STATUS_STYLE = {
  new: "bg-gold/15 text-gold-deep",
  contacted: "bg-sage/20 text-sage-deep",
  active: "bg-ink text-ivory-50",
  nurturing: "bg-ivory-200 text-ink-soft",
  archived: "bg-ivory-300 text-ink-muted",
};
const STATUS_LABEL = { new: "New", contacted: "Contacted", active: "Active", nurturing: "Nurturing", archived: "Archived" };
const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const shortDate = (d) => (d || "").slice(0, 10) || "—";
const TIMELINE_ICON = { joined: UserPlus, order: ShoppingBag, subscription: Repeat, application: FileText, reflection: Feather, intake: ClipboardList };

const SORTS = {
  last_activity: { label: "Last activity", get: (r) => r.last_activity || "" },
  created_at: { label: "Joined", get: (r) => r.created_at || "" },
  name: { label: "Name", get: (r) => (r.name || r.email || "").toLowerCase() },
  spend_cents: { label: "Spend", get: (r) => r.spend_cents || 0 },
  type: { label: "Type", get: (r) => r.type || "" },
};

export default function AdminCrm() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [type, setType] = useState(() => new URLSearchParams(window.location.search).get("type") || "all");
  const [status, setStatus] = useState("all");
  const [labelFilter, setLabelFilter] = useState("");
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState([]);
  const [labels, setLabels] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(() => new Set());
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_RECENT) || "[]"); } catch { return []; } });

  const [sel, setSel] = useState(null);
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [okToText, setOkToText] = useState(false);
  const [drawerTags, setDrawerTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [pinned, setPinned] = useState(false);
  const [grantBook, setGrantBook] = useState(false);
  const [grantVault, setGrantVault] = useState(false);
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMsg, setGrantMsg] = useState("");
  const [cleanup, setCleanup] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupReasons, setCleanupReasons] = useState([]);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [cStatus, setCStatus] = useState("new");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [nlExporting, setNlExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkLabel, setBulkLabel] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = React.useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (t, s, query, lab) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (t !== "all") params.set("type", t);
      if (s !== "all") params.set("status", s);
      if (query) params.set("q", query);
      if (lab) params.set("tag", lab);
      const { data } = await axios.get(`${API}/admin/crm/contacts?${params.toString()}`, auth);
      setRows(data.contacts || []);
      setSegments(data.segments || []);
      setLabels(data.labels || []);
      setTotal(data.total || 0);
      setTotalRevenue(data.total_revenue_cents || 0);
      setErr("");
    } catch (e) {
      setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load contacts.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!token) { setErr("Please sign in at /admin first."); return; }
    const h = setTimeout(() => load(type, status, q, labelFilter), q ? 300 : 0);
    return () => clearTimeout(h);
  }, [token, type, status, q, labelFilter, load]);

  const visible = useMemo(() => {
    let list = pinnedOnly ? rows.filter((r) => r.pinned) : rows;
    const getter = (SORTS[sortBy] || SORTS.last_activity).get;
    const dir = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = getter(a), vb = getter(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [rows, pinnedOnly, sortBy, sortDir]);
  const shown = visible.slice(0, 400);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir(key === "name" || key === "type" ? "asc" : "desc"); }
  };

  const pushRecent = (c) => {
    const entry = { email: c.email, name: c.name || c.email };
    setRecent((prev) => {
      const next = [entry, ...prev.filter((x) => x.email !== c.email)].slice(0, 8);
      localStorage.setItem(LS_RECENT, JSON.stringify(next));
      return next;
    });
  };

  const openContact = async (email, keepGrantMsg = false) => {
    try {
      const { data } = await axios.get(`${API}/admin/crm/contacts/${encodeURIComponent(email)}`, auth);
      setSel(data); setNotes(data.notes || ""); setPhone(data.phone || ""); setCStatus(data.status || "new");
      setOkToText(!!data.ok_to_text); setDrawerTags(data.tags || []); setPinned(!!data.pinned);
      const tiers = data.tiers || [];
      setGrantBook(tiers.includes("book_owner")); setGrantVault(tiers.includes("vault_member"));
      if (!keepGrantMsg) setGrantMsg("");
      setTagInput(""); setResendMsg("");
      pushRecent(data);
    } catch (e) { /* ignore */ }
  };

  const toggleSelect = (email) => {
    setSelected((prev) => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; });
  };
  const allShownSelected = shown.length > 0 && shown.every((r) => selected.has(r.email));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allShownSelected) shown.forEach((r) => n.delete(r.email));
      else shown.forEach((r) => n.add(r.email));
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const togglePinRow = async (r, e) => {
    e.stopPropagation();
    const next = !r.pinned;
    setRows((prev) => prev.map((x) => (x.email === r.email ? { ...x, pinned: next } : x)));
    try { await axios.patch(`${API}/admin/crm/contacts/${encodeURIComponent(r.email)}`, { pinned: next }, auth); }
    catch (e2) { setRows((prev) => prev.map((x) => (x.email === r.email ? { ...x, pinned: !next } : x))); }
  };

  const bulkApply = async (payload) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/admin/crm/bulk`, { emails: [...selected], ...payload }, auth);
      await load(type, status, q, labelFilter);
      setBulkStatus(""); setBulkLabel("");
    } catch (e) {
      if (e.response?.status === 401) { window.location.href = "/admin"; return; }
    } finally { setBulkBusy(false); }
  };

  const exportSelected = () => {
    const chosen = rows.filter((r) => selected.has(r.email));
    const head = ["name", "email", "phone", "type", "status", "labels", "joined", "last_activity", "spend_usd"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head.join(",")].concat(chosen.map((r) => [
      r.name || "", r.email, r.phone || "", r.type_label || r.type, r.status || "",
      (r.tags || []).join("|"), shortDate(r.created_at), shortDate(r.last_activity),
      ((r.spend_cents || 0) / 100).toFixed(2),
    ].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `crm-selected-${chosen.length}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (type !== "all") params.set("type", type);
      if (status !== "all") params.set("status", status);
      if (q) params.set("q", q);
      if (labelFilter) params.set("tag", labelFilter);
      const { data } = await axios.get(`${API}/admin/crm/contacts/export.csv?${params.toString()}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `crm-contacts-${type}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ } finally { setExporting(false); }
  };

  const exportNewsletterCsv = async () => {
    setNlExporting(true);
    try {
      const params = new URLSearchParams();
      if (type !== "all") params.set("type", type);
      if (status !== "all") params.set("status", status);
      if (q) params.set("q", q);
      if (labelFilter) params.set("tag", labelFilter);
      const { data } = await axios.get(`${API}/admin/crm/newsletter-export.csv?${params.toString()}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `newsletter-subscribers-${type}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ } finally { setNlExporting(false); }
  };

  const importCsv = async (f) => {
    if (!f) return;
    setImporting(true); setImportMsg("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/crm/import`, fd,
        { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setImportMsg(`Imported ${data.created} new · ${data.updated} updated · ${data.skipped} skipped`);
      load(type, status, q, labelFilter);
      setTimeout(() => setImportMsg(""), 6000);
    } catch (e) {
      if (e.response?.status === 401) { window.location.href = "/admin"; return; }
      setImportMsg(e.response?.data?.detail || "Import failed — please check the CSV format.");
      setTimeout(() => setImportMsg(""), 12000);
    } finally { setImporting(false); }
  };

  const addDrawerTag = () => {
    const t = tagInput.trim();
    if (!t || drawerTags.includes(t)) { setTagInput(""); return; }
    setDrawerTags((prev) => [...prev, t]); setTagInput("");
  };
  const removeDrawerTag = (t) => setDrawerTags((prev) => prev.filter((x) => x !== t));

  const grant = async () => {
    const tiers = [];
    if (grantBook) tiers.push("book_owner");
    if (grantVault) tiers.push("vault_member");
    if (tiers.length === 0) { setGrantMsg("Pick the Book, the Vault, or both."); return; }
    setGrantBusy(true); setGrantMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/crm/contacts/${encodeURIComponent(sel.email)}/grant`, { tiers }, auth);
      if (!data.new || data.new.length === 0) setGrantMsg(data.message || "Already has that access.");
      else {
        const r = data.result || {};
        setGrantMsg(r.email_status === "sent" ? `Access email sent (${r.template}).` : `Granted — but email ${r.email_status}: ${r.error || ""}`);
      }
      openContact(sel.email, true); load(type, status, q, labelFilter);
    } catch (e) {
      if (e.response?.status === 401) { window.location.href = "/admin"; return; }
      setGrantMsg(e.response?.data?.detail || "Could not grant access.");
    } finally { setGrantBusy(false); }
  };

  const openCleanup = async () => {
    setCleanup("loading");
    try {
      const { data } = await axios.get(`${API}/admin/crm/cleanup/preview`, auth);
      setCleanup(data);
      setCleanupReasons(Object.keys(data.counts).filter((k) => data.counts[k] > 0));
    } catch (e) { setCleanup(null); }
  };
  const runCleanup = async () => {
    const n = cleanupReasons.reduce((a, r) => a + (cleanup.counts[r] || 0), 0);
    if (n === 0) return;
    if (!window.confirm(`Permanently delete ${n} contact(s)? Clients with purchases/access are never included. This cannot be undone.`)) return;
    setCleanupBusy(true);
    try {
      await axios.post(`${API}/admin/crm/cleanup`, { reasons: cleanupReasons }, auth);
      setCleanup(null); load(type, status, q, labelFilter);
    } catch (e) { /* ignore */ } finally { setCleanupBusy(false); }
  };

  const saveContact = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/crm/contacts/${encodeURIComponent(sel.email)}`,
        { notes, phone, status: cStatus, ok_to_text: okToText, tags: drawerTags, pinned }, auth);
      setSel({ ...sel, notes, phone, status: cStatus, ok_to_text: okToText, tags: drawerTags, pinned });
      setSaved(true); setTimeout(() => setSaved(false), 1800);
      load(type, status, q, labelFilter);
    } catch (e) { /* ignore */ } finally { setSaving(false); }
  };

  const resendAccess = async () => {
    if (!sel?.access_code) return;
    setResending(true); setResendMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/codes/${encodeURIComponent(sel.access_code)}/resend-access`, {}, auth);
      const r = data.result || {};
      setResendMsg(`Sent — email ${r.email_status}, text ${r.text_status}.`);
      openContact(sel.email);
    } catch (e) {
      setResendMsg(e.response?.data?.detail || "Could not resend the access message.");
    } finally { setResending(false); }
  };

  if (err) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div><p className="text-ink-soft" data-testid="crm-error">{err}</p><Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link></div>
    </div>
  );

  const SortHead = ({ k, children, className = "" }) => (
    <button type="button" onClick={() => toggleSort(k)} data-testid={`crm-sort-${k}`}
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.12em] text-ink-muted hover:text-ink ${className}`}>
      {children}
      {sortBy === k ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
    </button>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-crm">
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <AdminNav />
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-ink">Contacts (CRM)</h1>
            <p className="mt-1 text-sm text-ink-muted" data-testid="crm-total">{total} people across the ecosystem</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="crm-import-input"
              onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ""; }} />
            <button type="button" onClick={openCleanup} data-testid="crm-cleanup-btn"
              className="inline-flex items-center gap-2 rounded-full bg-ivory-50 px-4 py-2 font-display text-sm text-terracotta ring-1 ring-ivory-400/60 hover:ring-terracotta/50">
              <Trash2 className="h-4 w-4" /> Clean up
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="crm-import-btn"
              className="inline-flex items-center gap-2 rounded-full bg-ivory-50 px-4 py-2 font-display text-sm text-ink ring-1 ring-ivory-400/60 hover:ring-gold/50 disabled:opacity-50">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import CSV
            </button>
            <button type="button" onClick={exportCsv} disabled={exporting} data-testid="crm-export-btn"
              className="inline-flex items-center gap-2 rounded-full bg-ivory-50 px-4 py-2 font-display text-sm text-ink ring-1 ring-ivory-400/60 hover:ring-gold/50 disabled:opacity-50">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
            </button>
            <button type="button" onClick={exportNewsletterCsv} disabled={nlExporting} data-testid="crm-newsletter-export-btn"
              title="Clean, deduped list ready for Mailchimp / ConvertKit"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
              {nlExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Newsletter CSV
            </button>
          </div>
        </div>
        {importMsg && <p className="mt-2 text-sm text-sage-deep" data-testid="crm-import-msg">{importMsg}</p>}

        {/* Summary bar */}
        <div className="mt-5 flex gap-3 overflow-x-auto pb-1" data-testid="crm-summary">
          <div className="flex min-w-[140px] flex-col rounded-2xl bg-ink px-4 py-3 text-ivory-50">
            <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] opacity-70"><Users className="h-3 w-3" /> Total</span>
            <span className="mt-1 font-display text-2xl" data-testid="crm-summary-total">{total}</span>
          </div>
          <div className="flex min-w-[150px] flex-col rounded-2xl bg-gold-deep px-4 py-3 text-ivory-50">
            <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] opacity-80"><CircleDollarSign className="h-3 w-3" /> Revenue</span>
            <span className="mt-1 font-display text-2xl" data-testid="crm-summary-revenue">{usd(totalRevenue)}</span>
          </div>
          {segments.map((s) => (
            <button key={s.type} type="button" onClick={() => setType(type === s.type ? "all" : s.type)} data-testid={`crm-summary-${s.type}`}
              className={`flex min-w-[150px] flex-col rounded-2xl px-4 py-3 text-left ring-1 transition ${type === s.type ? "bg-gold/15 ring-gold/50" : "bg-ivory-50 ring-ivory-400/60 hover:ring-gold/40"}`}>
              <span className="text-[11px] uppercase tracking-[0.12em] text-ink-muted">{s.label}</span>
              <span className="mt-1 font-display text-2xl text-ink">{s.count}</span>
              <span className="text-xs text-ink-muted">{usd(s.revenue_cents)}</span>
            </button>
          ))}
        </div>

        {/* Recently viewed */}
        {recent.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="crm-recent">
            <span className="flex items-center gap-1 text-[11px] uppercase tracking-[0.12em] text-ink-muted"><Clock className="h-3 w-3" /> Recent</span>
            {recent.map((r) => (
              <button key={r.email} type="button" onClick={() => openContact(r.email)} data-testid={`crm-recent-${r.email}`}
                className="truncate rounded-full bg-ivory-50 px-3 py-1 text-xs text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink">
                {r.name}
              </button>
            ))}
          </div>
        )}

        {/* Label filter */}
        {labels.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="crm-labels">
            <span className="flex items-center gap-1 text-[11px] uppercase tracking-[0.12em] text-ink-muted"><Tag className="h-3 w-3" /> Labels</span>
            {labels.map((l) => (
              <button key={l} type="button" onClick={() => setLabelFilter(labelFilter === l ? "" : l)} data-testid={`crm-label-${l}`}
                className={`rounded-full px-3 py-1 text-xs transition ${labelFilter === l ? "bg-gold-deep text-ivory-50" : "bg-gold/10 text-gold-deep hover:bg-gold/20"}`}>
                {l}
              </button>
            ))}
            {labelFilter && <button type="button" onClick={() => setLabelFilter("")} className="text-xs text-ink-muted underline" data-testid="crm-label-clear">clear</button>}
          </div>
        )}

        {/* Search + status + pinned */}
        <div className="mt-4 flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, label or note" data-testid="crm-search"
              className="w-full rounded-full border border-ivory-400/70 bg-ivory-50 py-2 pl-9 pr-9 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
            {q && (
              <button type="button" onClick={() => setQ("")} data-testid="crm-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-ink-muted hover:bg-ink/5 hover:text-ink"><X className="h-4 w-4" /></button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["all", ...Object.keys(STATUS_LABEL)].map((s) => (
              <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`crm-status-${s}`}
                className={`rounded-full px-3 py-1.5 text-xs transition ${status === s ? "bg-ink text-ivory-50" : "bg-ivory-50 text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink"}`}>
                {s === "all" ? "Any status" : STATUS_LABEL[s]}
              </button>
            ))}
            <button type="button" onClick={() => setPinnedOnly((v) => !v)} data-testid="crm-pinned-filter"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs transition ${pinnedOnly ? "bg-gold-deep text-ivory-50" : "bg-ivory-50 text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink"}`}>
              <Star className={`h-3.5 w-3.5 ${pinnedOnly ? "fill-ivory-50" : ""}`} /> Pinned
            </button>
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2.5 rounded-2xl bg-ink px-4 py-3 text-ivory-50" data-testid="crm-bulk-bar">
            <span className="font-display text-sm" data-testid="crm-bulk-count">{selected.size} selected</span>
            <select value={bulkStatus} onChange={(e) => { setBulkStatus(e.target.value); if (e.target.value) bulkApply({ status: e.target.value }); }}
              disabled={bulkBusy} data-testid="crm-bulk-status" className="rounded-full bg-ivory-50 px-3 py-1.5 text-xs text-ink">
              <option value="">Set status…</option>
              {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <div className="inline-flex items-center gap-1">
              <input value={bulkLabel} onChange={(e) => setBulkLabel(e.target.value)} placeholder="label" data-testid="crm-bulk-label-input"
                onKeyDown={(e) => { if (e.key === "Enter" && bulkLabel.trim()) bulkApply({ add_tags: [bulkLabel.trim()] }); }}
                className="w-28 rounded-full px-3 py-1.5 text-xs text-ink" />
              <button type="button" onClick={() => bulkLabel.trim() && bulkApply({ add_tags: [bulkLabel.trim()] })} disabled={bulkBusy || !bulkLabel.trim()}
                data-testid="crm-bulk-add-label" className="rounded-full bg-gold-deep px-3 py-1.5 text-xs disabled:opacity-50">Add label</button>
              <button type="button" onClick={() => bulkLabel.trim() && bulkApply({ remove_tags: [bulkLabel.trim()] })} disabled={bulkBusy || !bulkLabel.trim()}
                data-testid="crm-bulk-remove-label" className="rounded-full bg-ivory-50/20 px-3 py-1.5 text-xs disabled:opacity-50">Remove</button>
            </div>
            <button type="button" onClick={() => bulkApply({ pinned: true })} disabled={bulkBusy} data-testid="crm-bulk-pin" className="inline-flex items-center gap-1 rounded-full bg-ivory-50/20 px-3 py-1.5 text-xs"><Star className="h-3.5 w-3.5" /> Pin</button>
            <button type="button" onClick={() => bulkApply({ pinned: false })} disabled={bulkBusy} data-testid="crm-bulk-unpin" className="rounded-full bg-ivory-50/20 px-3 py-1.5 text-xs">Unpin</button>
            <button type="button" onClick={exportSelected} data-testid="crm-bulk-export" className="inline-flex items-center gap-1 rounded-full bg-ivory-50/20 px-3 py-1.5 text-xs"><Download className="h-3.5 w-3.5" /> Export</button>
            {bulkBusy && <Loader2 className="h-4 w-4 animate-spin" />}
            <button type="button" onClick={clearSelection} data-testid="crm-bulk-clear" className="ml-auto text-xs underline opacity-80 hover:opacity-100">Clear</button>
          </div>
        )}

        {/* Table */}
        <div className="mt-5 overflow-hidden rounded-2xl bg-ivory-50 shadow-sm ring-1 ring-ivory-400/60">
          {loading ? (
            <p className="flex items-center gap-2 p-6 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading contacts…</p>
          ) : shown.length === 0 ? (
            <p className="p-6 text-sm text-ink-muted" data-testid="crm-empty">{q ? `No contacts match “${q}”.` : "No contacts in this view."}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-ivory-400/50">
                    <th className="w-10 px-4 py-2.5">
                      <input type="checkbox" checked={allShownSelected} onChange={toggleSelectAll} data-testid="crm-select-all"
                        className="h-4 w-4 rounded border-ink/30 text-gold-deep focus:ring-gold/50" />
                    </th>
                    <th className="px-2 py-2.5"><SortHead k="name">Contact</SortHead></th>
                    <th className="px-2 py-2.5"><SortHead k="type">Type</SortHead></th>
                    <th className="px-2 py-2.5 text-[11px] uppercase tracking-[0.12em] text-ink-muted">Status</th>
                    <th className="px-2 py-2.5 text-[11px] uppercase tracking-[0.12em] text-ink-muted">Labels</th>
                    <th className="px-2 py-2.5"><SortHead k="created_at">Joined</SortHead></th>
                    <th className="px-2 py-2.5"><SortHead k="last_activity">Last</SortHead></th>
                    <th className="px-2 py-2.5"><SortHead k="spend_cents">Spend</SortHead></th>
                    <th className="w-8 px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.email} onClick={() => openContact(r.email)} data-testid={`crm-row-${r.email}`}
                      className="cursor-pointer border-b border-ivory-400/40 last:border-0 hover:bg-gold/5">
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.email)} onChange={() => toggleSelect(r.email)} data-testid={`crm-select-${r.email}`}
                          className="h-4 w-4 rounded border-ink/30 text-gold-deep focus:ring-gold/50" />
                      </td>
                      <td className="min-w-0 px-2 py-3">
                        <p className="truncate font-display text-sm text-ink">{r.name || r.email}</p>
                        <p className="truncate text-xs text-ink-muted">{r.email}</p>
                      </td>
                      <td className="px-2 py-3"><span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ${TYPE_STYLE[r.type] || "bg-ivory-200 text-ink-muted"}`}>{r.type_label}</span></td>
                      <td className="px-2 py-3"><span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ${STATUS_STYLE[r.status] || "bg-ivory-200 text-ink-muted"}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                      <td className="px-2 py-3">
                        <div className="flex max-w-[180px] flex-wrap gap-1">
                          {(r.tags || []).slice(0, 3).map((t) => <span key={t} className="truncate rounded-full bg-gold/10 px-2 py-0.5 text-[10px] text-gold-deep">{t}</span>)}
                          {(r.tags || []).length > 3 && <span className="text-[10px] text-ink-muted">+{r.tags.length - 3}</span>}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-xs text-ink-muted">{shortDate(r.created_at)}</td>
                      <td className="whitespace-nowrap px-2 py-3 text-xs text-ink-muted">{shortDate(r.last_activity)}</td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-ink">{r.spend_cents ? usd(r.spend_cents) : "—"}</td>
                      <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={(e) => togglePinRow(r, e)} data-testid={`crm-pin-${r.email}`} title={r.pinned ? "Unpin" : "Pin"}
                          className={`rounded-full p-1 ${r.pinned ? "text-gold-deep" : "text-ink-muted/40 hover:text-ink-muted"}`}>
                          <Star className={`h-4 w-4 ${r.pinned ? "fill-gold-deep" : ""}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-ivory-400/50 px-5 py-2 text-[11px] uppercase tracking-[0.14em] text-ink-muted" data-testid="crm-count">
                {visible.length} {q || labelFilter || pinnedOnly ? "matching" : "shown"}{visible.length > 400 ? " · showing first 400" : ""}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Contact detail drawer */}
      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={() => setSel(null)} data-testid="crm-drawer">
          <div className="h-full w-full max-w-md overflow-y-auto bg-ivory-50 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 truncate font-display text-2xl text-ink">
                  {sel.name || sel.email}
                  <button type="button" onClick={() => setPinned((v) => !v)} data-testid="crm-drawer-pin" className={pinned ? "text-gold-deep" : "text-ink-muted/40 hover:text-ink-muted"}>
                    <Star className={`h-5 w-5 ${pinned ? "fill-gold-deep" : ""}`} />
                  </button>
                </h2>
                <p className="flex items-center gap-1.5 text-sm text-ink-muted"><Mail className="h-3.5 w-3.5" />{sel.email}</p>
              </div>
              <button type="button" onClick={() => setSel(null)} data-testid="crm-drawer-close" className="rounded-full p-1.5 text-ink-soft hover:text-ink"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className={`rounded-full px-2.5 py-0.5 text-xs ${TYPE_STYLE[sel.type] || "bg-ivory-200 text-ink-muted"}`}>{sel.type_label}</span>
              {sel.source && <span className="rounded-full bg-ivory-200 px-2.5 py-0.5 text-xs text-ink-muted">via {sel.source}</span>}
            </div>

            {/* Labels editor */}
            <section className="mt-5">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-ink-muted"><Tag className="h-3 w-3" /> Labels</p>
              <div className="flex flex-wrap items-center gap-1.5">
                {drawerTags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-gold/10 px-2.5 py-0.5 text-xs text-gold-deep" data-testid={`crm-drawer-tag-${t}`}>
                    {t}<button type="button" onClick={() => removeDrawerTag(t)} data-testid={`crm-drawer-tag-remove-${t}`} className="hover:text-gold"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <span className="inline-flex items-center gap-1">
                  <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDrawerTag(); } }}
                    placeholder="add label" data-testid="crm-drawer-tag-input" className="w-24 rounded-full border border-ivory-400/70 bg-[#FCFBF7] px-2.5 py-1 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                  <button type="button" onClick={addDrawerTag} data-testid="crm-drawer-tag-add" className="rounded-full bg-ink p-1 text-ivory-50"><Plus className="h-3 w-3" /></button>
                </span>
              </div>
              <p className="mt-1.5 text-[11px] text-ink-muted">Changes save when you tap Save below.</p>
            </section>

            {/* Grant access + access-email status */}
            <section className="mt-5" data-testid="crm-grant-section">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-ink-muted"><KeyRound className="h-3 w-3" /> Access</p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-ivory-200 px-3 py-1.5 text-xs text-ink">
                  <input type="checkbox" checked={grantBook} onChange={(e) => setGrantBook(e.target.checked)} data-testid="crm-grant-book" className="h-3.5 w-3.5 rounded border-ink/30 text-gold-deep focus:ring-gold/50" /> The Book
                </label>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-ivory-200 px-3 py-1.5 text-xs text-ink">
                  <input type="checkbox" checked={grantVault} onChange={(e) => setGrantVault(e.target.checked)} data-testid="crm-grant-vault" className="h-3.5 w-3.5 rounded border-ink/30 text-gold-deep focus:ring-gold/50" /> The Vault
                </label>
                <button type="button" onClick={grant} disabled={grantBusy} data-testid="crm-grant-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs text-ivory-50 disabled:opacity-50">
                  {grantBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Grant &amp; email
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-ink-muted">Saving sends the access email right away — only for newly-granted access.</p>
              {grantMsg && <p className="mt-1 text-xs text-ink-soft" data-testid="crm-grant-msg">{grantMsg}</p>}
              {sel.access_email_status && (
                <p className="mt-2 text-xs" data-testid="crm-access-status">
                  <span className={sel.access_email_status === "sent" ? "text-sage-deep" : "text-terracotta"}>
                    Last access email: {sel.access_email_status}{sel.access_email_type ? ` (${sel.access_email_type})` : ""}
                  </span>
                  {sel.access_email_at ? ` · ${(sel.access_email_at || "").slice(0, 16).replace("T", " ")}` : ""}
                  {sel.access_email_error ? <span className="block text-terracotta">{sel.access_email_error}</span> : null}
                </p>
              )}
            </section>

            <section className="mt-5 grid grid-cols-1 gap-4">
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-ink-muted"><Phone className="h-3 w-3" /> Phone</p>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Add a phone number" data-testid="crm-phone"
                  className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-sm text-ink-soft" data-testid="crm-ok-to-text-label">
                  <input type="checkbox" checked={okToText} onChange={(e) => setOkToText(e.target.checked)} data-testid="crm-ok-to-text"
                    className="h-4 w-4 rounded border-ink/30 text-gold-deep focus:ring-gold/50" />
                  <MessageSquare className="h-3.5 w-3.5 text-gold-deep" /> OK to text
                </label>
                {sel.access_code && (
                  <div className="mt-3">
                    <button type="button" onClick={resendAccess} disabled={resending} data-testid="crm-resend-access"
                      className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3.5 py-1.5 text-xs text-gold-deep transition hover:bg-gold/10 disabled:opacity-50">
                      {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Resend access message
                    </button>
                    {resendMsg && <span className="ml-2 text-xs text-ink-muted" data-testid="crm-resend-msg">{resendMsg}</span>}
                  </div>
                )}
              </div>
              <div>
                <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">Status</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.keys(STATUS_LABEL).map((s) => (
                    <button key={s} type="button" onClick={() => setCStatus(s)} data-testid={`crm-set-status-${s}`}
                      className={`rounded-full px-3 py-1.5 text-xs transition ${cStatus === s ? "bg-ink text-ivory-50" : "bg-ivory-200 text-ink-soft hover:text-ink"}`}>
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {sel.timeline && sel.timeline.length > 0 && (
              <section className="mt-6" data-testid="crm-timeline">
                <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-ink-muted"><Clock className="h-3 w-3" /> Journey timeline</p>
                <ol className="relative ml-1 border-l border-ivory-400/70 pl-4">
                  {sel.timeline.map((t, i) => {
                    const Icon = TIMELINE_ICON[t.kind] || Clock;
                    return (
                      <li key={i} className="relative mb-3 last:mb-0" data-testid={`crm-timeline-item-${i}`}>
                        <span className="absolute -left-[22px] flex h-4 w-4 items-center justify-center rounded-full bg-gold/15 text-gold-deep"><Icon className="h-2.5 w-2.5" /></span>
                        <p className="text-sm text-ink">{t.label}{t.amount_cents ? ` · ${usd(t.amount_cents)}` : ""}</p>
                        {t.date && <p className="text-[11px] text-ink-muted">{(t.date || "").slice(0, 10)}</p>}
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}

            {sel.orders && sel.orders.length > 0 && (
              <section className="mt-5">
                <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Orders</p>
                {sel.orders.map((o) => (
                  <div key={o.id} className="mt-2 rounded-lg bg-ivory-200/60 px-3 py-2 text-sm text-ink">
                    {(o.items || []).map((i) => i.title).join(", ") || o.offer} · {usd(o.total_cents)}
                  </div>
                ))}
              </section>
            )}
            {sel.subscriptions && sel.subscriptions.length > 0 && (
              <section className="mt-5">
                <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Subscriptions</p>
                {sel.subscriptions.map((s) => (
                  <div key={s.id} className="mt-2 rounded-lg bg-ivory-200/60 px-3 py-2 text-sm text-ink">{s.plan_name} · <span className="text-ink-soft">{s.status}</span></div>
                ))}
              </section>
            )}
            {sel.applications && sel.applications.length > 0 && (
              <section className="mt-5">
                <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Immersion applications</p>
                {sel.applications.map((a) => (
                  <div key={a.id} className="mt-2 rounded-lg bg-ivory-200/60 px-3 py-2 text-sm text-ink">{(a.created_at || "").slice(0, 10)} · <span className="text-ink-soft">{a.status}</span></div>
                ))}
              </section>
            )}

            <section className="mt-6">
              <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">Private notes</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="crm-notes"
                className="w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
            </section>

            <button type="button" onClick={saveContact} disabled={saving} data-testid="crm-save"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
            {saved && <span className="ml-2 text-sm text-sage-deep" data-testid="crm-saved">Saved</span>}
          </div>
        </div>
      )}

      {/* Cleanup modal */}
      {cleanup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/50 px-5" onClick={() => setCleanup(null)} data-testid="crm-cleanup-modal">
          <div className="w-full max-w-md rounded-2xl bg-ivory-50 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="flex items-center gap-2 font-display text-xl text-ink"><Trash2 className="h-5 w-5 text-terracotta" /> Clean up contacts</h3>
            {cleanup === "loading" ? (
              <p className="mt-4 flex items-center gap-2 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Finding bad addresses…</p>
            ) : cleanup.total === 0 ? (
              <p className="mt-4 text-sm text-ink-soft" data-testid="crm-cleanup-empty">Nothing to clean — no malformed, fake, or bounced addresses found. Clients are always protected.</p>
            ) : (
              <>
                <p className="mt-2 text-sm text-ink-muted">These are never real clients (anyone with a purchase or access is excluded). Deletion is permanent.</p>
                <div className="mt-4 space-y-2">
                  {[["invalid_format", "Malformed addresses"], ["fake_domain", "Fake / test domains"], ["hard_bounce", "Mailgun hard bounces"]].map(([key, label]) => (
                    cleanup.counts[key] > 0 && (
                      <label key={key} className="flex cursor-pointer items-center justify-between rounded-xl bg-ivory-100 px-4 py-3" data-testid={`crm-cleanup-reason-${key}`}>
                        <span className="flex items-center gap-2 text-sm text-ink">
                          <input type="checkbox" checked={cleanupReasons.includes(key)}
                            onChange={(e) => setCleanupReasons((prev) => e.target.checked ? [...prev, key] : prev.filter((x) => x !== key))}
                            className="h-4 w-4 rounded border-ink/30 text-terracotta focus:ring-terracotta/50" />
                          {label}
                        </span>
                        <span className="font-display text-lg text-ink">{cleanup.counts[key]}</span>
                      </label>
                    )
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button type="button" onClick={() => setCleanup(null)} className="rounded-full px-4 py-2 text-sm text-ink-soft hover:text-ink" data-testid="crm-cleanup-cancel">Cancel</button>
                  <button type="button" onClick={runCleanup} disabled={cleanupBusy || cleanupReasons.length === 0} data-testid="crm-cleanup-confirm"
                    className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
                    {cleanupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete {cleanupReasons.reduce((a, r) => a + (cleanup.counts[r] || 0), 0)} permanently
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
